import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  Slide,
  Avatar,
  Card,
  CardContent,
  useTheme,
  Tooltip,
  Alert,
  CircularProgress
} from "@mui/material";
import {
  Add,
  Print,
  Delete,
  Payment,
  Visibility,
  Close,
  AttachMoney,
  CreditCard,
  AccountBalance,
  QrCode,
  AccountBalanceWallet,
  ReceiptLong,
  CheckCircle,
  Cancel,
  CalendarMonth
} from "@mui/icons-material";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import moment from "moment";

// --- REDUX IMPORTS ---
import { useSelector, useDispatch } from "react-redux";
// Make sure these paths match your actual folder structure
// import { listAllReceipts, addNewReceipt } from "../../store/actions/bookingActions"; 
import ReceiptDocument from "./ReceiptDocument"; 
import { addNewReceipt, listAllReceipts } from "../../../store/actions";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ... (Keep getPaymentIcon and StatCard helpers exactly as they were) ...
const getPaymentIcon = (mode, colors) => {
    switch(mode) {
        case 'cash': return <Avatar sx={{ bgcolor: colors.greenAccent[600], width: 30, height: 30 }}><AttachMoney sx={{ fontSize: 18 }} /></Avatar>;
        case 'card': return <Avatar sx={{ bgcolor: colors.blueAccent[600], width: 30, height: 30 }}><CreditCard sx={{ fontSize: 18 }} /></Avatar>;
        case 'upi': return <Avatar sx={{ bgcolor: colors.greenAccent[500], width: 30, height: 30 }}><QrCode sx={{ fontSize: 18 }} /></Avatar>;
        case 'bank_transfer': return <Avatar sx={{ bgcolor: colors.grey[500], width: 30, height: 30 }}><AccountBalance sx={{ fontSize: 18 }} /></Avatar>;
        case 'cheque': return <Avatar sx={{ bgcolor: colors.grey[700], width: 30, height: 30 }}><ReceiptLong sx={{ fontSize: 18 }} /></Avatar>;
        default: return <Avatar sx={{ bgcolor: colors.primary[300], width: 30, height: 30 }}><AccountBalanceWallet sx={{ fontSize: 18 }} /></Avatar>;
    }
};

const StatCard = ({ title, value, icon, color, subColor }) => (
    <Card sx={{ backgroundColor: color, color: '#fff', minWidth: 200, flex: 1, boxShadow: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: '16px !important' }}>
            <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: 'uppercase', fontSize: '0.75rem' }}>{title}</Typography>
                <Typography variant="h4" fontWeight="bold">₹ {value ? value.toLocaleString() : "0"}</Typography>
            </Box>
            <Box sx={{ p: 1, borderRadius: '50%', backgroundColor: subColor, display: 'flex' }}>
                {icon}
            </Box>
        </CardContent>
    </Card>
);

const AdvancePaymentTab = ({ bookingDetails, colors }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // --- 1. REDUX STATE ---
  // Accessing the 'receipts' array directly as per your reducer logic
  const receiptsList = useSelector((state) => state.booking.receipts);
  // Ensure it's always an array to prevent crashes
  const receipts = Array.isArray(receiptsList) ? receiptsList : [];
  
  const loading = useSelector((state) => state.booking.loading); // Assuming you have a loading flag
  const hotelProfile = useSelector(state => state.registration?.hotelProfile);

  // --- 2. LOCAL UI STATE ---
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null); 
  
  const [formData, setFormData] = useState({
    amount: "",
    date: moment().format("YYYY-MM-DD"),
    mode: "cash",
    transaction_ref: "",
    collected_by: "",
    bank_name: "",       
    instrument_date: "",  
    remarks: "",
  });

  // --- 3. INITIAL FETCH ---
  useEffect(() => {
    if (bookingDetails?.booking_id) {
        // Fetch receipts whenever the booking ID changes
        dispatch(listAllReceipts({ booking_id: bookingDetails.booking_id }));
    }
  }, [dispatch, bookingDetails?.booking_id]);


  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mode") {
        setFormData({
            ...formData,
            mode: value,
            transaction_ref: "",
            bank_name: "",
            instrument_date: "",
            collected_by: "" 
        });
    } else {
        setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddAdvance = async () => {
    // 1. Validation
    if(!formData.amount || formData.amount <= 0) {
        alert("Please enter a valid amount");
        return;
    }
    if (formData.mode === 'cash' && !formData.collected_by) {
        alert("For Cash payments, 'Collected By' is required.");
        return;
    }
    if (formData.mode !== 'cash' && !formData.transaction_ref) {
        alert("Please enter the Transaction Reference.");
        return;
    }

    // 2. Prepare Payload
    const payload = {
        booking_id: bookingDetails?.booking_id,
        hotel_id: bookingDetails?.hotel_id,
        amount: Number(formData.amount),
        date: moment(formData.date).valueOf(), 
        mode: formData.mode,
        transaction_ref: formData.mode === 'cash' ? null : formData.transaction_ref,
        bank_name: formData.bank_name,
        instrument_date: formData.instrument_date ? moment(formData.instrument_date).valueOf() : null,
        collected_by: formData.mode === 'cash' ? formData.collected_by : null,
        remarks: formData.remarks,
    };

    console.log("Dispatching Advance:", payload);
    
    const success = await dispatch(addNewReceipt(payload));

    if (success) {
        setOpenAddModal(false);
        setFormData({ amount: "", date: moment().format("YYYY-MM-DD"), mode: "cash", transaction_ref: "", collected_by: "", bank_name: "", instrument_date: "", remarks: "" });
    } else {
        alert("Failed to add receipt. Please try again.");
    }
  };

  const handleDelete = (id) => {
      // You need to create a deleteReceipt action for this to work with Redux
      if(window.confirm("Are you sure you want to void this receipt?")) {
          // dispatch(deleteReceipt(id)); 
          alert("Delete functionality needs backend integration.");
      }
  };

  const handlePreview = (receipt) => {
      setSelectedReceipt(receipt);
      setOpenPreviewModal(true);
  };

  const getRefLabel = (mode) => {
      switch(mode) {
          case 'cheque': return 'Cheque Number';
          case 'bank_transfer': return 'UTR / IMPS Reference';
          case 'card': return 'Auth Code / Last 4 Digits';
          case 'upi': return 'UPI Reference ID';
          case 'ota': return 'Booking Reference ID';
          default: return 'Transaction Reference';
      }
  };

  const showBankFields = ['cheque', 'bank_transfer'].includes(formData.mode);
  const showDateFields = ['cheque'].includes(formData.mode);
  const isCash = formData.mode === 'cash';

  // --- CALCULATIONS FROM REDUX DATA ---
  // Ensure we sum only active receipts
  const totalPaid = receipts.filter(r => r.is_active).reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalDue = (bookingDetails?.total_price || 0) - totalPaid;

  return (
    <Box>
      {/* 1. SUMMARY CARDS */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={4}>
          <StatCard 
            title="Total Booking Value" 
            value={bookingDetails?.total_price || 0} 
            icon={<ReceiptLong />} 
            color={colors.primary[400]}
            subColor={colors.primary[500]} 
          />
          <StatCard 
            title="Total Collected" 
            value={totalPaid} 
            icon={<CheckCircle />} 
            color={colors.greenAccent[600]}
            subColor={colors.greenAccent[700]} 
          />
          <StatCard 
            title="Balance Pending" 
            value={totalDue} 
            icon={<Cancel />} 
            color={colors.redAccent[600]}
            subColor={colors.redAccent[700]} 
          />
      </Stack>

      {/* 2. HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} mt={4}>
        <Box>
            <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>Payment History</Typography>
            <Typography variant="body2" color={colors.grey[400]}>Manage advance receipts and transactions</Typography>
        </Box>
        <Button 
            variant="contained" 
            sx={{ 
                backgroundColor: colors.blueAccent[600], 
                '&:hover': { backgroundColor: colors.blueAccent[700] },
                fontWeight: 'bold',
                px: 3
            }}
            startIcon={<Add />} 
            onClick={() => setOpenAddModal(true)}
            disabled={bookingDetails?.status === 'cancelled'}
        >
            New Payment
        </Button>
      </Stack>

      {/* 3. DATA TABLE (Redux Driven) */}
      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400], borderRadius: 2, boxShadow: 4 }}>
        {loading ? (
             <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress color="secondary" /></Box>
        ) : (
        <Table>
          <TableHead sx={{ backgroundColor: colors.blueAccent[800] }}>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Receipt Details</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Payment Mode & Ref</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Details</TableCell>
              <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receipts.length > 0 ? (
                receipts.map((row) => (
                <TableRow 
                    key={row._id} // Using the real MongoDB _id from your JSON
                    sx={{ 
                        opacity: row.is_active ? 1 : 0.6,
                        '&:hover': { backgroundColor: colors.primary[500] },
                        transition: 'background-color 0.2s',
                        borderBottom: `1px solid ${colors.primary[500]}`
                    }}
                >
                    {/* Date & Receipt No */}
                    <TableCell>
                        <Stack>
                            <Typography variant="body2" fontWeight="bold" color={colors.greenAccent[400]}>
                                {row.receipt_no}
                            </Typography>
                            <Typography variant="caption" color={colors.grey[400]}>
                                {/* Handling string epoch from JSON */}
                                {moment(Number(row.date)).format("DD MMM, YYYY")}
                            </Typography>
                        </Stack>
                    </TableCell>

                    {/* Mode with Icon & Ref */}
                    <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            {getPaymentIcon(row.mode, colors)}
                            <Box>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{row.mode}</Typography>
                                {row.transaction_ref && (
                                    <Typography variant="caption" color={colors.grey[300]} sx={{ display: 'block' }}>
                                        Ref: {row.transaction_ref}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </TableCell>

                    {/* Extra Details */}
                    <TableCell>
                        <Stack spacing={0.5}>
                            {row.mode === 'cash' ? (
                                <Typography variant="caption" color={colors.grey[300]}>
                                    Collected by: {row.collected_by}
                                </Typography>
                            ) : (
                                <>
                                    {row.bank_name && (
                                        <Typography variant="caption" color={colors.grey[200]} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccountBalance sx={{ fontSize: 14 }}/> {row.bank_name}
                                        </Typography>
                                    )}
                                    {row.instrument_date && (
                                        <Typography variant="caption" color={colors.grey[400]} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarMonth sx={{ fontSize: 14 }}/> {moment(Number(row.instrument_date)).format("DD MMM")}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Stack>
                    </TableCell>

                    {/* Amount */}
                    <TableCell align="right">
                        <Typography variant="h6" fontWeight="bold" color={row.is_active ? colors.greenAccent[500] : colors.grey[500]}>
                            ₹ {row.amount.toLocaleString()}
                        </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                        <Chip 
                            label={row.is_active ? "Success" : "Voided"} 
                            size="small" 
                            variant="filled"
                            sx={{ 
                                bgcolor: row.is_active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                color: row.is_active ? '#4caf50' : '#f44336',
                                fontWeight: 'bold',
                                border: 'none'
                            }} 
                        />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                        <Stack direction="row" justifyContent="center" spacing={0}>
                             {row.is_active && (
                                <Tooltip title="Preview Receipt">
                                    <IconButton size="small" onClick={() => handlePreview(row)}>
                                        <Visibility fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                             )}

                             {row.is_active && (
                                 <PDFDownloadLink
                                    document={<ReceiptDocument receipt={row} bookingDetails={bookingDetails} hotelProfile={hotelProfile} />}
                                    fileName={`${row.receipt_no}.pdf`}
                                 >
                                    {({ loading }) => (
                                        <Tooltip title="Download PDF">
                                            <IconButton size="small" color="secondary" disabled={loading}>
                                                <Print fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                 </PDFDownloadLink>
                             )}
                        </Stack>
                    </TableCell>
                </TableRow>
                ))
            ) : (
                <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <ReceiptLong sx={{ fontSize: 40, color: colors.grey[500], mb: 1 }} />
                        <Typography color="textSecondary">No advance payments recorded.</Typography>
                    </TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </TableContainer>

      {/* ADD ADVANCE MODAL (No changes needed here, keeping logic same as before) */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: colors.blueAccent[700], color: '#fff' }}>
            New Advance Payment
        </DialogTitle>
        <DialogContent sx={{ mt: 2, backgroundColor: colors.primary[500], pt: 3 }}>
            <Box component="form" sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Payment Date"
                            type="date"
                            name="date"
                            fullWidth
                            variant="outlined"
                            value={formData.date}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Payment Mode"
                            select
                            name="mode"
                            fullWidth
                            variant="outlined"
                            value={formData.mode}
                            onChange={handleChange}
                        >
                            {['cash', 'card', 'upi', 'bank_transfer', 'cheque', 'ota'].map((option) => (
                                <MenuItem key={option} value={option}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                         <Typography sx={{ textTransform: 'capitalize' }}>{option}</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Amount Collected"
                            type="number"
                            name="amount"
                            fullWidth
                            variant="filled"
                            color="secondary"
                            value={formData.amount}
                            onChange={handleChange}
                            InputProps={{ 
                                startAdornment: <Typography sx={{ mr: 1, color: colors.greenAccent[500], fontWeight: 'bold' }}>₹</Typography>,
                                sx: { fontSize: 20, fontWeight: 'bold' } 
                            }}
                        />
                    </Grid>

                    {/* Conditional Fields */}
                    {isCash && (
                        <Grid item xs={12}>
                             <Alert severity="info" sx={{ mb: 1, py: 0 }}>Cash payments must track the staff member.</Alert>
                            <TextField
                                label="Collected By (Staff Name)"
                                name="collected_by"
                                fullWidth
                                required
                                variant="outlined"
                                value={formData.collected_by}
                                onChange={handleChange}
                            />
                        </Grid>
                    )}

                    {!isCash && (
                        <Grid item xs={12}>
                            <TextField
                                label={getRefLabel(formData.mode)}
                                name="transaction_ref"
                                fullWidth
                                required
                                variant="outlined"
                                value={formData.transaction_ref}
                                onChange={handleChange}
                                placeholder={formData.mode === 'cheque' ? "e.g. 001234" : "e.g. Transaction ID"}
                            />
                        </Grid>
                    )}

                    {showBankFields && (
                         <Grid item xs={12} sm={showDateFields ? 6 : 12}>
                            <TextField
                                label="Bank Name (Sender)"
                                name="bank_name"
                                fullWidth
                                variant="outlined"
                                value={formData.bank_name}
                                onChange={handleChange}
                                placeholder="e.g. HDFC, SBI"
                            />
                        </Grid>
                    )}

                    {showDateFields && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Cheque Date"
                                type="date"
                                name="instrument_date"
                                fullWidth
                                variant="outlined"
                                value={formData.instrument_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            label="Remarks / Note"
                            name="remarks"
                            fullWidth
                            multiline
                            rows={2}
                            variant="outlined"
                            value={formData.remarks}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: colors.primary[500] }}>
            <Button onClick={() => setOpenAddModal(false)} sx={{ color: colors.grey[300] }}>Cancel</Button>
            <Button 
                onClick={handleAddAdvance} 
                variant="contained" 
                color="secondary" 
                startIcon={<Payment />}
                sx={{ px: 4, fontWeight: 'bold' }}
            >
                Confirm Payment
            </Button>
        </DialogActions>
      </Dialog>

      {/* PREVIEW MODAL */}
      <Dialog fullScreen open={openPreviewModal} onClose={() => setOpenPreviewModal(false)} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative', backgroundColor: colors.blueAccent[700] }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setOpenPreviewModal(false)} aria-label="close">
                <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Receipt Preview</Typography>
            <Button autoFocus color="inherit" onClick={() => setOpenPreviewModal(false)}>Close</Button>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 0, height: '100%', backgroundColor: '#525659' }}>
           {selectedReceipt && (
               <PDFViewer width="100%" height="100%" style={{ border: "none" }}>
                  <ReceiptDocument 
                      receipt={selectedReceipt} 
                      bookingDetails={bookingDetails} 
                      hotelProfile={hotelProfile} 
                  />
               </PDFViewer>
           )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdvancePaymentTab;